import { Dispatch } from 'redux';

import loadingStatuses from '@constants/loadingStatuses';

import { addNotification } from '@redux/actions/notifications.actions';
import {
  createOrUpdatePayoutSettingsApi,
  getPublisherBillingAddressApi,
  getPublisherBillingDetailsApi,
  PayoutSettingsInput,
  removePayoutSettingsApi,
  resendPayoutValidationCodeApi,
  updatePublisherBillingAddressApi,
  verifyPayoutValidationCodeApi,
} from '@api/billing-publisher.api';
import {
  BillingPublisherAddressType,
  BillingPublisherPayoutSettingsResponseType,
  BillingPublisherType,
} from '@ts/billing-publisher.types';
import {
  setPublisherBillingAddressData,
  setPublisherBillingAddressLs,
  setPublisherBillingData,
  setPublisherBillingLs,
} from '@redux/actions/billing-publisher.actions';
import { BillingPublisherPaymentMethodMap } from 'respona_api/generated/common_pb';

type PaymentMethodValue = BillingPublisherPaymentMethodMap[keyof BillingPublisherPaymentMethodMap];

export const fetchPublisherBillingDetails =
  (silent = false) =>
  (dispatch: Dispatch): Promise<BillingPublisherType> => {
    !silent && dispatch(setPublisherBillingLs(loadingStatuses.INITED));

    return getPublisherBillingDetailsApi()
      .then((billing) => {
        dispatch(setPublisherBillingData(billing));
        return billing;
      })
      .finally(() => {
        dispatch(setPublisherBillingLs(loadingStatuses.LOADED));
      });
  };

export const fetchBillingAddress =
  () =>
  (dispatch: Dispatch): void => {
    dispatch(setPublisherBillingAddressLs(loadingStatuses.INITED));

    getPublisherBillingAddressApi()
      .then((address) => {
        dispatch(setPublisherBillingAddressData(address));
      })
      .finally(() => {
        dispatch(setPublisherBillingAddressLs(loadingStatuses.LOADED));
      });
  };

export const updatePublisherBillingAddress =
  (addressFields: BillingPublisherAddressType, callback: () => void) =>
  (dispatch: Dispatch): Promise<void> => {
    return updatePublisherBillingAddressApi(addressFields).then((updateAddress) => {
      dispatch(setPublisherBillingAddressData(updateAddress));
      dispatch(addNotification({ title: 'Billing address updated', type: 'success' }));
      callback();
    });
  };

export const updatePublisherPayoutSettings =
  (input: PayoutSettingsInput) =>
  (dispatch: Dispatch): Promise<BillingPublisherPayoutSettingsResponseType> => {
    return createOrUpdatePayoutSettingsApi(input).then(async (response) => {
      const billing = await getPublisherBillingDetailsApi();
      dispatch(setPublisherBillingData(billing));

      if (response.isSuccess) {
        dispatch(
          addNotification({
            title: 'The verification code has been sent to your email.',
            type: 'warning',
          })
        );
      } else {
        // When the backend reports per-field validation errors we leave the
        // detailed messages to the form (it highlights the offending inputs);
        // the toast just nudges the user to look at the form.
        const hasFieldErrors = (response.fieldErrorsList?.length ?? 0) > 0;
        const errorTitle = hasFieldErrors
          ? response.message || 'Please review the highlighted fields and try again.'
          : response.message || 'Could not save payout settings. Please try again.';
        dispatch(addNotification({ title: errorTitle, type: 'error' }));
      }

      return response;
    });
  };

export const removePublisherPayoutSettings =
  (method: PaymentMethodValue) =>
  (dispatch: Dispatch): Promise<void> => {
    return removePayoutSettingsApi(method)
      .then(async () => {
        const billing = await getPublisherBillingDetailsApi();
        dispatch(setPublisherBillingData(billing));
        dispatch(addNotification({ title: 'Payout method removed', type: 'success' }));
      })
      .catch((error) => {
        dispatch(
          addNotification({
            title: error?.message || 'Failed to remove payout method',
            type: 'error',
          })
        );
        throw error;
      });
  };

export const verifyPublisherPayoutValidationCode =
  (verificationCode: string, method: PaymentMethodValue, callback: () => void) =>
  (dispatch: Dispatch): Promise<BillingPublisherPayoutSettingsResponseType> => {
    return verifyPayoutValidationCodeApi(verificationCode, method).then(async (response) => {
      const billing = await getPublisherBillingDetailsApi();
      dispatch(setPublisherBillingData(billing));

      if (response.isSuccess) {
        dispatch(
          addNotification({
            title: 'Verification successful. Your payout method has been updated.',
            type: 'success',
          })
        );
        callback();
      } else if (response.message) {
        dispatch(addNotification({ title: response.message, type: 'error' }));
      }

      return response;
    });
  };

export const resendPublisherPayoutValidationCode =
  () =>
  (dispatch: Dispatch): Promise<BillingPublisherPayoutSettingsResponseType> => {
    return resendPayoutValidationCodeApi().then((response) => {
      if (response.isSuccess) {
        dispatch(
          addNotification({
            title: 'A new verification code has been sent to your email.',
            type: 'warning',
          })
        );
      } else if (response.message) {
        dispatch(addNotification({ title: response.message, type: 'error' }));
      }

      return response;
    });
  };
